/**
 * -------------------------------------------------------------------------------------------
 * Copyright (c) Microsoft Corporation.  All Rights Reserved.  Licensed under the MIT License.
 * See License in the project root for license information.
 * -------------------------------------------------------------------------------------------
 */
import { EventDispatcher } from '../utils/EventDispatcher';
/**
 * Provider Type to be extended for implmenting new providers
 *
 * @export
 * @abstract
 * @class IProvider
 * @implements {AuthenticationProvider}
 */
export class IProvider {
    /**
     * returns state of Provider
     *
     * @readonly
     * @type {ProviderState}
     * @memberof IProvider
     */
    get state() {
        return this._state;
    }
    /**
     * Incremental consent setting
     *
     * @readonly
     * @memberof IProvider
     */
    get isIncrementalConsentDisabled() {
        return this._isIncrementalConsentDisabled;
    }
    /**
     * Enable/Disable incremental consent
     *
     * @readonly
     * @memberof IProvider
     */
    set isIncrementalConsentDisabled(disabled) {
        this._isIncrementalConsentDisabled = disabled;
    }
    /**
     * Name used for analytics
     *
     * @readonly
     * @memberof IProvider
     */
    get name() {
        return 'MgtIProvider';
    }
    constructor() {
        /**
         * Enable/Disable multi account functionality
         *
         * @protected
         * @type {boolean}
         * @memberof IProvider
         */
        this.isMultipleAccountDisabled = true;
        this._loginChangedDispatcher = new EventDispatcher();
        this._activeAccountChangedDispatcher = new EventDispatcher();
        /**
         * Enable/Disable incremental consent
         *
         * @protected
         * @type {boolean}
         * @memberof IProvider
         */
        this._isIncrementalConsentDisabled = false;
        this._state = ProviderState.Loading;
    }
    /**
     * sets state of Provider and fires loginchangedDispatcher
     *
     * @param {ProviderState} state
     * @memberof IProvider
     */
    setState(state) {
        if (state !== this._state) {
            this._state = state;
            this._loginChangedDispatcher.fire({});
        }
    }
    /**
     * event handler when login changes
     *
     * @param {EventHandler<LoginChangedEvent>} eventHandler
     * @memberof IProvider
     */
    onStateChanged(eventHandler) {
        this._loginChangedDispatcher.add(eventHandler);
    }
    /**
     * removes event handler for when login changes
     *
     * @param {EventHandler<LoginChangedEvent>} eventHandler
     * @memberof IProvider
     */
    removeStateChangedHandler(eventHandler) {
        this._loginChangedDispatcher.remove(eventHandler);
    }
    /**
     * Switch between two signed in accounts
     *
     * @param {*} user
     * @memberof IProvider
     */
    setActiveAccount(user) {
        this.fireActiveAccountChanged();
    }
    /**
     * Event handler when Active account changes
     *
     * @param {EventHandler<ActiveAccountChanged>} eventHandler
     * @memberof IProvider
     */
    onActiveAccountChanged(eventHandler) {
        this._activeAccountChangedDispatcher.add(eventHandler);
    }
    /**
     * Removes event handler for when Active account changes
     *
     * @param {EventHandler<ActiveAccountChanged>} eventHandler
     * @memberof IProvider
     */
    removeActiveAccountChangedHandler(eventHandler) {
        this._activeAccountChangedDispatcher.remove(eventHandler);
    }
    /**
     * Fires event when active account changes
     *
     * @memberof IProvider
     */
    fireActiveAccountChanged() {
        this._activeAccountChangedDispatcher.fire({});
    }
    /**
     * uses scopes to recieve access token
     *
     * @param {...string[]} scopes
     * @returns {Promise<string>}
     * @memberof IProvider
     */
    getAccessTokenForScopes(...scopes) {
        return this.getAccessToken({ scopes });
    }
}
/**
 * LoginType
 *
 * @export
 * @enum {number}
 */
export var LoginType;
(function (LoginType) {
    /**
     * Popup = 0
     */
    LoginType[LoginType["Popup"] = 0] = "Popup";
    /**
     * Redirect = 1
     */
    LoginType[LoginType["Redirect"] = 1] = "Redirect";
})(LoginType || (LoginType = {}));
/**
 * ProviderState
 *
 * @export
 * @enum {number}
 */
export var ProviderState;
(function (ProviderState) {
    /**
     * Loading = 0
     */
    ProviderState[ProviderState["Loading"] = 0] = "Loading";
    /**
     * SignedOut = 1
     */
    ProviderState[ProviderState["SignedOut"] = 1] = "SignedOut";
    /**
     * SignedIn = 2
     */
    ProviderState[ProviderState["SignedIn"] = 2] = "SignedIn";
})(ProviderState || (ProviderState = {}));
//# sourceMappingURL=IProvider.js.map